/**
 * ProcessMediaPage — режим 1 «Поиск фото» + режим 2 «Генерация картинок».
 *
 * ТЗ media-photos 2026-09-22. Бэкенд: /api/v1/media/* (очередь media,
 * PipelineRun pipeline_type="media", честные сметы upper-bound, статусы §8).
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { extractApiError } from '@/lib/apiError';
import { useDatasetPreview } from '@/hooks/useFiles';
import { ProcessControlBar, type ProcessExecutionState } from '@/components/process/ProcessControlBar';
import { clampTestRunLimit, useMediaStore, type MediaMode } from '@/store/mediaStore';
import {
  useMediaGenEstimate,
  useMediaGenStart,
  useMediaRunCancel,
  useMediaRunResults,
  useMediaRunStatus,
  useMediaSearchEstimate,
  useMediaSearchStart,
  useMediaStyles,
  type MediaRunStatus,
} from '@/hooks/useMedia';
import { MediaResultsGrid } from '@/components/media/MediaResultsGrid';

const MODES: { key: MediaMode; titleKey: string; descKey: string }[] = [
  { key: 'image_search', titleKey: 'media.modes.search', descKey: 'media.modes.search_desc' },
  { key: 'image_generation', titleKey: 'media.modes.generation', descKey: 'media.modes.generation_desc' },
];

const RUN_STATUS_TO_EXECUTION: Record<
  MediaRunStatus['status'],
  'queued' | 'running' | 'completed' | 'cancelled' | 'failed'
> = {
  queued: 'queued',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

export function ProcessMediaPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const mode = useMediaStore((s) => s.mode);
  const setMode = useMediaStore((s) => s.setMode);
  const datasetId = useMediaStore((s) => s.datasetId);
  const setDatasetId = useMediaStore((s) => s.setDatasetId);
  const tierKey = useMediaStore((s) => s.tierKey);
  const setTierKey = useMediaStore((s) => s.setTierKey);
  const isTestRun = useMediaStore((s) => s.isTestRun);
  const setIsTestRun = useMediaStore((s) => s.setIsTestRun);
  const testRunLimit = useMediaStore((s) => s.testRunLimit);
  const setTestRunLimit = useMediaStore((s) => s.setTestRunLimit);
  const currentRunId = useMediaStore((s) => s.currentRunId);
  const setCurrentRunId = useMediaStore((s) => s.setCurrentRunId);
  const userNote = useMediaStore((s) => s.userNote);
  const setUserNote = useMediaStore((s) => s.setUserNote);
  const userText = useMediaStore((s) => s.userText);
  const setUserText = useMediaStore((s) => s.setUserText);
  const style = useMediaStore((s) => s.style);
  const setStyle = useMediaStore((s) => s.setStyle);
  const selectedColumns = useMediaStore((s) => s.selectedColumns);
  const setSelectedColumns = useMediaStore((s) => s.setSelectedColumns);
  const toggleColumn = useMediaStore((s) => s.toggleColumn);
  const imageCount = useMediaStore((s) => s.imageCount);
  const setImageCount = useMediaStore((s) => s.setImageCount);

  // Deep link from Files page: /process/media?dataset=...&tier=...&test=...
  useEffect(() => {
    const dataset = searchParams.get('dataset');
    if (dataset) setDatasetId(dataset);
    const tier = searchParams.get('tier');
    if (tier === 'economy' || tier === 'optimal' || tier === 'max') setTierKey(tier);
    const test = searchParams.get('test');
    if (test !== null) setIsTestRun(test === '1');
  }, [searchParams, setDatasetId, setTierKey, setIsTestRun]);

  const { data: preview } = useDatasetPreview(datasetId);
  const columns = useMemo(() => {
    const out: string[] = [];
    for (const row of (preview?.rows ?? []).slice(0, 5)) {
      for (const key of Object.keys(row.raw_data ?? {})) {
        if (!out.includes(key)) out.push(key);
      }
    }
    return out;
  }, [preview]);

  const isSearch = mode === 'image_search';
  const stylesQuery = useMediaStyles(tierKey, !isSearch && !!datasetId);
  const styles = stylesQuery.data?.styles ?? [];
  const imagesPerRow = stylesQuery.data?.images_per_row ?? 1;

  const estimateBody = useMemo(
    () => ({ dataset_id: datasetId ?? '', tier_key: tierKey, is_test_run: isTestRun }),
    [datasetId, tierKey, isTestRun],
  );
  const searchEstimate = useMediaSearchEstimate(isSearch ? estimateBody : null);
  const genEstimate = useMediaGenEstimate(isSearch ? null : estimateBody);

  const startSearch = useMediaSearchStart();
  const startGen = useMediaGenStart();
  const cancelRun = useMediaRunCancel();
  const statusQuery = useMediaRunStatus(currentRunId);
  const resultsQuery = useMediaRunResults(currentRunId);
  const run = statusQuery.data ?? null;

  const startPending = startSearch.isPending || startGen.isPending;
  const startError =
    extractApiError(startSearch.error, undefined) ?? extractApiError(startGen.error, undefined);

  const estimateLine = isSearch
    ? searchEstimate.data
      ? `${t('media.estimate.upper')}: ${searchEstimate.data.total_credits_upper} · ${searchEstimate.data.pricing}`
      : null
    : genEstimate.data
      ? `${t('media.estimate.upper')}: ${genEstimate.data.total_credits} · ${genEstimate.data.pricing}`
      : null;

  const handleStart = () => {
    if (!datasetId || startPending) return;
    const base = {
      dataset_id: datasetId,
      tier_key: tierKey,
      is_test_run: isTestRun,
      test_run_limit: clampTestRunLimit(testRunLimit),
      request_id:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
    };
    const onStarted = (r: MediaRunStatus) => setCurrentRunId(r.run_id);
    if (isSearch) {
      startSearch.mutate({ ...base, user_note: userNote }, { onSuccess: onStarted });
    } else {
      startGen.mutate(
        {
          ...base,
          user_text: userText,
          style: style || undefined,
          selected_columns: selectedColumns.length > 0 ? selectedColumns : undefined,
          image_count: imageCount || imagesPerRow,
        },
        { onSuccess: onStarted },
      );
    }
  };

  const executionState: ProcessExecutionState = {
    status: run ? RUN_STATUS_TO_EXECUTION[run.status] : 'idle',
    isStarting: startPending,
    canStart: !!datasetId,
    startLabel: isTestRun
      ? t('media.start_test', { defaultValue: 'Test run' })
      : t('media.start_full', { defaultValue: 'Full run' }),
    onStart: handleStart,
    onCancel: currentRunId ? () => cancelRun.mutate(currentRunId) : undefined,
    cancelPending: cancelRun.isPending,
    progress:
      run && run.total_rows > 0
        ? { processed: run.processed_rows, total: run.total_rows }
        : undefined,
    isTestRun,
    errorMessage: startError ?? run?.error_message ?? null,
  };

  const resultRows = useMemo(() => {
    const data = resultsQuery.data;
    if (!data) return [];
    return Object.values(data.rows).sort((a, b) => a.row_index - b.row_index);
  }, [resultsQuery.data]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold mb-1">{t('media.page_title')}</h1>

      {/* Режим — два плоских режима модуля (ТЗ §1): переключатель над баром. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {MODES.map((m) => {
          const active = m.key === mode;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`text-left p-4 rounded-xl border transition-colors ${
                active
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-400'
                  : 'border-surface-200 bg-surface-50 hover:border-surface-300'
              }`}
            >
              <div className="font-semibold text-sm">{t(m.titleKey)}</div>
              <div className="text-xs text-surface-500 mt-1">{t(m.descKey)}</div>
            </button>
          );
        })}
      </div>

      <ProcessControlBar
        datasetId={datasetId}
        setDatasetId={setDatasetId}
        tierKey={tierKey}
        setTierKey={setTierKey}
        testRunLimit={testRunLimit}
        setTestRunLimit={setTestRunLimit}
        isTestRun={isTestRun}
        setIsTestRun={setIsTestRun}
        costEstimate={estimateLine}
        execution={executionState}
      />

      {/* Смета (честная upper-bound, ТЗ §биллинг). */}
      {estimateLine ? (
        <div className="text-xs text-surface-500 px-1">{estimateLine}</div>
      ) : null}

      {/* Inputs по режиму. */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{isSearch ? t('media.modes.search') : t('media.modes.generation')}</h2>
        </div>
        <div className="card-body space-y-4">
          {isSearch ? (
            <div>
              <label className="text-xs font-medium text-surface-600 block mb-1" htmlFor="media-user-note">
                {t('media.user_note')}
              </label>
              <textarea
                id="media-user-note"
                className="w-full text-sm rounded-lg border border-surface-200 p-2 min-h-[64px]"
                maxLength={2000}
                value={userNote}
                onChange={(e) => setUserNote(e.target.value)}
                placeholder={t('media.user_note_hint')}
              />
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-surface-600 block mb-1" htmlFor="media-user-text">
                  {t('media.user_text')}
                </label>
                <textarea
                  id="media-user-text"
                  className="w-full text-sm rounded-lg border border-surface-200 p-2 min-h-[64px]"
                  maxLength={4000}
                  value={userText}
                  onChange={(e) => setUserText(e.target.value)}
                  placeholder={t('media.user_text_hint')}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-surface-600 block mb-1" htmlFor="media-style">
                    {t('media.style')}
                  </label>
                  <select
                    id="media-style"
                    className="w-full text-sm rounded-lg border border-surface-200 p-2"
                    value={style}
                    onChange={(e) => setStyle(e.target.value)}
                  >
                    <option value="">{t('media.style_none')}</option>
                    {styles.map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                    <option value="custom">{t('media.style_custom')}</option>
                  </select>
                  {stylesQuery.isError ? (
                    <p className="text-xs text-red-500 mt-1">
                      {extractApiError(stylesQuery.error, undefined) ??
                        t('media.styles_unavailable')}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="text-xs font-medium text-surface-600 block mb-1" htmlFor="media-image-count">
                    {t('media.image_count')}
                  </label>
                  <input
                    id="media-image-count"
                    type="number"
                    min={1}
                    max={10}
                    value={imageCount || imagesPerRow}
                    onChange={(e) => setImageCount(Number(e.target.value))}
                    className="w-full text-sm rounded-lg border border-surface-200 p-2"
                  />
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-surface-600 block mb-1">
                  {t('media.selected_columns')}
                </span>
                <div className="flex flex-wrap gap-2">
                  {columns.map((col) => {
                    const on = selectedColumns.includes(col);
                    return (
                      <button
                        key={col}
                        type="button"
                        onClick={() => toggleColumn(col)}
                        className={`px-2 py-1 rounded-full text-xs border ${
                          on
                            ? 'bg-primary-500 border-primary-500 text-white'
                            : 'bg-surface-50 border-surface-200 text-surface-600'
                        }`}
                      >
                        {col}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 flex gap-2">
                  {selectedColumns.length > 0 ? (
                    <button type="button" className="text-xs text-surface-500 underline" onClick={() => setSelectedColumns([])}>
                      {t('media.clear_columns')}
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Прогон: прогресс + результаты (честные статусы строк). */}
      {currentRunId ? (
        <div className="space-y-3">
          {run ? (
            <div className="card">
              <div className="card-body py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    run.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : run.status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : run.status === 'cancelled'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {t(`media.run_status.${run.status}`, { defaultValue: run.status })}
                </span>
                <span className="text-xs text-surface-500">
                  {t('media.run_status.processed', {
                    processed: run.processed_rows,
                    total: run.total_rows,
                  })}
                </span>
                <span className="text-xs text-surface-500">
                  {t('media.run_status.spent', { credits: run.spent_credits })}
                </span>
                {run.is_test_run ? (
                  <span className="text-[11px] text-surface-400">{t('media.test_run_tag')}</span>
                ) : null}
              </div>
              {run.progress.length > 0 ? (
                <div className="px-4 pb-3 max-h-40 overflow-y-auto">
                  {run.progress.slice(-12).map((line, i) => (
                    <div key={line + i} className="text-xs text-surface-400">
                      {line}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {resultsQuery.data ? (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">{t('media.results_title')}</h2>
              </div>
              <div className="card-body">
                <MediaResultsGrid rows={resultRows} mode={mode} />
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-surface-400 text-center py-8 text-sm">
            {t('media.no_results_yet')}
          </div>
        </div>
      )}
    </div>
  );
}
